import * as React from 'react';
import Typography from '@/components/sushi/Typography';
import { styled } from '../../../stitches.config';
import { createTransition } from '@/utils/css/css';

export type Tab = { label: React.ReactNode; children: React.ReactNode };

export type TabsFooterProps = {
  tabs: Tab[];
  activeTabIndex: number;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
};

export type TabsProps = {
  tabs: Tab[];
  Footer?: (props: TabsFooterProps) => React.ReactElement;
};

export const Tabs = ({ tabs, Footer }: TabsProps) => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  return (
    <TabsWrapper>
      <TabsHeader>
        {tabs.map((tab, index) => (
          <TabsTab
            key={index}
            active={index === activeTabIndex}
            onClick={() => setActiveTabIndex(index)}>
            <Typography variant="bodyM" color="current" center font="montreal">
              {tab.label}
            </Typography>
          </TabsTab>
        ))}
        <TabsFiller />
      </TabsHeader>
      <TabsBody>{tabs[activeTabIndex].children}</TabsBody>
      {Footer ? (
        <TabsFooter>
          <Footer
            tabs={tabs}
            activeTabIndex={activeTabIndex}
            setActiveTabIndex={setActiveTabIndex}
          />
        </TabsFooter>
      ) : null}
    </TabsWrapper>
  );
};

const TabsWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 800,
  padding: '0 48px',
  marginBottom: 72,
});

const TabsHeader = styled('header', {
  display: 'flex',
  alignItems: 'flex-end',
});

const TabsTab = styled('button', {
  color: '$textSecondary',
  appearance: 'none',
  border: 'none',
  backgroundColor: 'transparent',
  padding: '0 12px 12px',
  borderBottom: '1px solid $borderSecondary',
  transition: createTransition(['border-color', 'color'], '0.15s', 'ease'),

  '&:focus-visible': {
    color: '$textPrimary',
    outline: 'none',
  },

  variants: {
    active: {
      true: {
        color: '$textPrimary',
        borderBottom: '1px solid $borderSelected',
      },
    },
  },
});

const TabsFiller = styled('div', {
  flex: 1,
  borderBottom: '1px solid $colors$borderSecondary',
});

const TabsBody = styled('div', {});

const TabsFooter = styled('footer', { marginTop: 32 });
